<template>
  <div class="analytics-tab">
    <recognition-filters
      :programs="programs"
      :program-id="programId"
      :selected-program="selectedProgram"
      :selected-date-range="selectedDateRange"
      :date-range-options="RECOGNITION_ANALYTICS_DATE_RANGES"
      :has-active-filters="hasActiveFilters"
      @update:selected-program="selectedProgram = $event"
      @update:selected-date-range="selectedDateRange = $event"
      @reset="resetFilters"
    >
      <template #prepend>
        <b-form-select
          v-if="hasDivisions"
          v-model="selectedDivision"
          :options="divisionOptions"
          class="recognition-filters__filter"
        />
      </template>
    </recognition-filters>

    <b-alert v-if="error && !loading" variant="danger" show>
      Unable to load analytics data. Please try adjusting your filters or try again later.
    </b-alert>
    <template v-else>
      <!-- Reward Spend -->
      <reward-spend-section
        :summary="summary"
        :spend-over-time="spendOverTime"
        :spend-over-time-by-category="spendOverTimeByCategory"
        :has-spend-categories="hasSpendCategories"
        :loading="loading"
      />

      <!-- Reward Redemption -->
      <reward-redemption-section :redemption="redemption" />

      <!-- Employee Engagement -->
      <employee-engagement-section :engagement="engagement" />

      <!-- Top Employees Tables -->
      <b-row class="analytics-tab__tables-row">
        <b-col cols="12" lg="7">
          <top-employees-table :employees="topEmployees" :loading="loading" />
        </b-col>
        <b-col cols="12" lg="5">
          <top-recognisers-table :recognisers="topRecognisers" :loading="loading" />
        </b-col>
      </b-row>

      <!-- Qualities Recognised -->
      <qualities-section
        :qualities="qualities"
        :loading="loading"
        :program-ids="lastFetch.programIds"
        :params="lastFetch.params"
      />
    </template>
  </div>
</template>

<script setup>
import axios from 'axios';
import debounce from 'lodash/debounce';
import { ref, computed, watch, onMounted, onBeforeUnmount, reactive } from 'vue';
import RecognitionFilters from '@/pages/recognition/components/RecognitionFilters.vue';
import RewardSpendSection from '@/pages/recognition/components/analytics/RewardSpendSection.vue';
import RewardRedemptionSection from '@/pages/recognition/components/analytics/RewardRedemptionSection.vue';
import EmployeeEngagementSection from '@/pages/recognition/components/analytics/EmployeeEngagementSection.vue';
import TopEmployeesTable from '@/pages/recognition/components/analytics/TopEmployeesTable.vue';
import TopRecognisersTable from '@/pages/recognition/components/analytics/TopRecognisersTable.vue';
import QualitiesSection from '@/pages/recognition/components/analytics/QualitiesSection.vue';
import { RECOGNITION_ANALYTICS_DATE_RANGES, getDateFromForRange } from '@/pages/recognition/constants';
import { collectSpendCategories } from '@/pages/recognition/utils/spendCategories';
import {
  transformSummary,
  transformSpendOverTime,
  transformSpendOverTimeByCategory,
  transformRedemptionBreakdown,
  transformEngagement,
  transformTopEmployees,
  transformTopRecognisers,
  transformQualities,
} from '@/pages/recognition/utils/analyticsTransformers';
import useDivisionSelection from '@/state/composables/useDivisionSelection';
import ApiClient from '@/ApiClient';

const props = defineProps({
  programs: {
    type: Array,
    default: () => [],
  },
  programId: {
    type: String,
    default: null,
  },
});

const { divisions, hasDivisions } = useDivisionSelection();

const selectedDivision = ref(null);
const selectedProgram = ref(null);
const selectedDateRange = ref('last_12_months');

const activeProgramId = computed(() => props.programId || selectedProgram.value);

const summary = ref(undefined);
const spendOverTime = ref(null);
const spendOverTimeByCategory = ref(null);
const redemption = ref(undefined);
const engagement = ref(undefined);
const topEmployees = ref([]);
const topRecognisers = ref([]);
const qualities = ref(undefined);
const loading = ref(false);
const error = ref(false);

// Snapshot of the inputs used for the most recent successful fetch. The qualities section
// passes these through to QualityBreakdownCard so each card can lazy-load its own
// value-distribution without re-deriving filter state.
const lastFetch = reactive({ programIds: [], params: {} });

let cancelSource = null;

const divisionOptions = computed(() => {
  const options = [{ value: null, text: 'Entire company' }];
  for (const division of divisions.value) {
    options.push({
      value: division.division_id,
      text: division.name,
    });
  }
  return options;
});

// Only the programs the chart is actually reporting on, so the category dimension is
// offered based on those rather than on anything the company has configured elsewhere.
const scopedPrograms = computed(() =>
  activeProgramId.value
    ? props.programs.filter(program => program.recognition_award_program_id === activeProgramId.value)
    : props.programs,
);

// Gated on program config, not on the response: every grant without a category lands in
// one "Uncategorised" bucket, so a company that never configured categories would still
// come back with a dataset and be offered a breakdown that says nothing. Same source of
// truth as the category filters on the activity and transactions tables.
const hasSpendCategories = computed(() => collectSpendCategories(scopedPrograms.value).length > 0);

const hasActiveFilters = computed(() => {
  const hasDivisionFilter = selectedDivision.value !== null;
  const hasProgramFilter = !props.programId && selectedProgram.value !== null;
  const hasDateFilter = selectedDateRange.value !== 'last_12_months';
  return hasDivisionFilter || hasProgramFilter || hasDateFilter;
});

const buildParams = () => {
  const params = {};
  if (selectedDivision.value) {
    params.division_ids = [selectedDivision.value];
  }
  const startDate = getDateFromForRange(selectedDateRange.value);
  if (startDate) {
    params.start_date = startDate;
  }
  const now = new Date();
  params.end_date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
    now.getDate(),
  ).padStart(2, '0')}`;
  return params;
};

const fetchAnalytics = async () => {
  const programIds = activeProgramId.value
    ? [activeProgramId.value]
    : props.programs.map(p => p.recognition_award_program_id);

  if (programIds.length === 0) {
    return;
  }

  cancelSource?.cancel('superseded');
  cancelSource = axios.CancelToken.source();
  const token = cancelSource.token;

  loading.value = true;
  error.value = false;
  // Reset every section's data so child components flip back to their loading skeleton
  // while the new fetch is in flight. Without this, the previous program's stats stay
  // visible until the response arrives, which masks the filter change.
  summary.value = undefined;
  spendOverTime.value = null;
  spendOverTimeByCategory.value = null;
  redemption.value = undefined;
  engagement.value = undefined;
  topEmployees.value = [];
  topRecognisers.value = [];
  qualities.value = undefined;

  try {
    const params = buildParams();
    const result = await ApiClient.getRecognitionAnalyticsBatch(programIds, params, { cancelToken: token });

    summary.value = transformSummary(result.summary);
    spendOverTime.value = transformSpendOverTime(result.spend_over_time);
    spendOverTimeByCategory.value = transformSpendOverTimeByCategory(result.spend_over_time_by_category);
    redemption.value = transformRedemptionBreakdown(result.redemption_breakdown);
    engagement.value = transformEngagement(result.engagement);
    topEmployees.value = transformTopEmployees(result.top_recognised);
    topRecognisers.value = transformTopRecognisers(result.top_recognisers);
    qualities.value = transformQualities(result.company_values);

    lastFetch.programIds = [...programIds];
    lastFetch.params = { ...params };
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }
    console.error('Analytics fetch error:', e);
    summary.value = null;
    spendOverTime.value = null;
    spendOverTimeByCategory.value = null;
    redemption.value = null;
    engagement.value = null;
    topEmployees.value = [];
    topRecognisers.value = [];
    qualities.value = null;
    error.value = true;
  } finally {
    loading.value = false;
  }
};

const resetFilters = () => {
  selectedDivision.value = null;
  selectedProgram.value = null;
  selectedDateRange.value = 'last_12_months';
};

const debouncedFetch = debounce(() => fetchAnalytics(), 400);

watch([selectedDivision, activeProgramId, selectedDateRange], () => {
  debouncedFetch();
});

onMounted(() => {
  fetchAnalytics();
});

onBeforeUnmount(() => {
  debouncedFetch.cancel();
  cancelSource?.cancel('component unmount');
});
</script>

<style scoped lang="scss">
.analytics-tab {
  &__tables-row {
    margin-bottom: 1.5rem;

    --bs-gutter-x: 1rem;
    --bs-gutter-y: 1rem;
  }
}
</style>
